// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import Authentication from '@app/features/auth/state/Authentication';
import * as PrivateChannelCommands from '@app/features/channel/commands/PrivateChannelCommands';
import {canReportMessage} from '@app/features/channel/components/MessageActionUtils';
import styles from '@app/features/guild/components/modals/guild_tabs/GuildMemberActionsSheet.module.css';
import {TransferOwnershipModal} from '@app/features/guild/components/modals/TransferOwnershipModal';
import {ManageRolesBottomSheet} from '@app/features/guild/components/RoleManagement';
import Guilds from '@app/features/guild/state/Guilds';
import {
	resolveGuildModerationCapabilities,
	resolveGuildScopedModerationActionKeys,
} from '@app/features/guild/utils/GuildModerationCapabilityUtils';
import type {GuildMember} from '@app/features/member/models/GuildMember';
import type {Message} from '@app/features/messaging/models/Message';
import {BanMemberModal} from '@app/features/moderation/components/modals/BanMemberModal';
import {KickMemberModal} from '@app/features/moderation/components/modals/KickMemberModal';
import {RemoveTimeoutModal} from '@app/features/moderation/components/modals/RemoveTimeoutModal';
import {TimeoutMemberSheet} from '@app/features/moderation/components/modals/TimeoutMemberSheet';
import {openReportMessageModal, openReportUserModal} from '@app/features/moderation/utils/ReportActionUtils';
import {useRoleHierarchy} from '@app/features/permissions/hooks/useRoleHierarchy';
import Permission from '@app/features/permissions/state/Permission';
import * as PermissionUtils from '@app/features/permissions/utils/PermissionUtils';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as RelationshipCommands from '@app/features/relationship/commands/RelationshipCommands';
import Relationships from '@app/features/relationship/state/Relationships';
import * as RelationshipActionUtils from '@app/features/relationship/utils/RelationshipActionUtils';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import {StatusAwareAvatar} from '@app/features/ui/components/StatusAwareAvatar';
import {MenuBottomSheet, type MenuGroupType} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import {ChangeNicknameModal} from '@app/features/user/components/modals/ChangeNicknameModal';
import type {User} from '@app/features/user/models/User';
import UserProfileMobile from '@app/features/user/state/UserProfileMobile';
import Users from '@app/features/user/state/Users';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import * as CallUtils from '@app/features/voice/utils/CallUtils';
import {Permissions} from '@app/vendor/constants/ChannelConstants';
import {RelationshipTypes} from '@app/vendor/constants/UserConstants';
import {useLingui} from '@lingui/react/macro';
import {
	ChatTeardropIcon,
	ClockIcon,
	CrownIcon,
	FlagIcon,
	GavelIcon,
	IdentificationCardIcon,
	PencilIcon,
	ProhibitIcon,
	SignOutIcon,
	UserCircleIcon,
	UserGearIcon,
	UserMinusIcon,
	UserPlusIcon,
	VideoCameraIcon,
} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type {FC} from 'react';
import {useState} from 'react';

const logger = new Logger('GuildMemberActionsSheet');
interface GuildMemberActionsSheetProps {
	isOpen: boolean;
	onClose: () => void;
	user: User;
	member: GuildMember;
	guildId: string;
	message?: Message;
}
export const GuildMemberActionsSheet: FC<GuildMemberActionsSheetProps> = observer(
	({isOpen, onClose, user, member, guildId, message}) => {
		const {t, i18n} = useLingui();
		const currentUserId = Authentication.currentUserId;
		const isCurrentUser = user.id === currentUserId;
		const isBot = user.bot;
		const currentUserUnclaimed = !(Users.currentUser?.isClaimed() ?? true);
		const relationship = Relationships.getRelationship(user.id);
		const relationshipType = relationship?.type;
		const isBlocked = relationshipType === RelationshipTypes.BLOCKED;
		const canKickMembers = Permission.can(Permissions.KICK_MEMBERS, {guildId});
		const canBanMembers = Permission.can(Permissions.BAN_MEMBERS, {guildId});
		const canModerateMembers = Permission.can(Permissions.MODERATE_MEMBERS, {guildId});
		const guild = Guilds.getGuild(guildId);
		const guildSnapshot = guild?.toJSON();
		const {canManageTarget} = useRoleHierarchy(guild);
		const targetHasModerateMembersPermission =
			guildSnapshot !== undefined && PermissionUtils.can(Permissions.MODERATE_MEMBERS, user.id, guildSnapshot);
		const {canKick, canBan, canTimeout} = resolveGuildModerationCapabilities({
			isCurrentUser,
			canManageTarget: canManageTarget(user.id),
			canKickMembers,
			canBanMembers,
			canModerateMembers,
			targetHasModerateMembersPermission,
		});
		const memberIsTimedOut = member.isTimedOut();
		const isOwner = guild?.ownerId === currentUserId;
		const canTransfer = !isCurrentUser && !isBot && isOwner;
		const hasRoles = guild && Object.values(guild.roles).some((r) => !r.isEveryone);
		const [manageRolesSheetOpen, setManageRolesSheetOpen] = useState(false);
		const hasChangeNicknamePermission = Permission.can(Permissions.CHANGE_NICKNAME, {guildId});
		const hasManageNicknamesPermission = Permission.can(Permissions.MANAGE_NICKNAMES, {guildId});
		const canManageNicknames =
			(isCurrentUser && hasChangeNicknamePermission && !memberIsTimedOut) ||
			(!isCurrentUser && hasManageNicknamesPermission);
		const handleViewProfile = () => {
			onClose();
			UserProfileMobile.open(user.id, guildId);
		};
		const openDmChannel = async () => {
			try {
				await PrivateChannelCommands.openDMChannel(user.id);
			} catch (error) {
				logger.error('Failed to open DM channel', error);
			}
		};
		const handleMessage = async () => {
			onClose();
			await openDmChannel();
		};
		const handleOpenBlockedDm = () => {
			onClose();
			ModalCommands.push(
				modal(() => (
					<ConfirmModal
						title={t`Open DM`}
						description={t`You blocked ${user.username}. You won't be able to send messages unless you unblock them.`}
						primaryText={t`Open DM`}
						primaryVariant="primary"
						onPrimary={openDmChannel}
					/>
				)),
			);
		};
		const handleStartVoiceCall = async () => {
			onClose();
			try {
				const channelId = await PrivateChannelCommands.ensureDMChannel(user.id);
				await CallUtils.requestStartCall(i18n, channelId, {kind: 'voice'});
			} catch (error) {
				logger.error('Failed to start voice call', error);
			}
		};
		const handleChangeNickname = () => {
			onClose();
			ModalCommands.push(modal(() => <ChangeNicknameModal guildId={guildId} user={user} member={member} />));
		};
		const handleSendFriendRequest = () => {
			RelationshipCommands.sendFriendRequest(user.id);
			onClose();
		};
		const handleAcceptFriendRequest = () => {
			RelationshipActionUtils.showAcceptFriendRequestConfirmation(i18n, user);
			onClose();
		};
		const handleRemoveFriend = () => {
			onClose();
			RelationshipActionUtils.showRemoveFriendConfirmation(i18n, user);
		};
		const handleBlockUser = () => {
			onClose();
			RelationshipActionUtils.showBlockUserConfirmation(i18n, user);
		};
		const handleUnblockUser = () => {
			onClose();
			RelationshipActionUtils.showUnblockUserConfirmation(i18n, user);
		};
		const handleKickMember = () => {
			onClose();
			ModalCommands.push(modal(() => <KickMemberModal guildId={guildId} targetUser={user} />));
		};
		const handleBanMember = () => {
			onClose();
			ModalCommands.push(modal(() => <BanMemberModal guildId={guildId} targetUser={user} />));
		};
		const handleCopyUserId = () => {
			TextCopyCommands.copy(i18n, user.id, true);
			onClose();
		};
		const handleManageRoles = () => {
			setManageRolesSheetOpen(true);
		};
		const handleTransferOwnership = () => {
			onClose();
			ModalCommands.push(
				modal(() => <TransferOwnershipModal guildId={guildId} targetUser={user} targetMember={member} />),
			);
		};
		const handleReportUser = () => {
			onClose();
			openReportUserModal({i18n, user, guildId, message});
		};
		const handleReportMessage = () => {
			if (!message || !canReportMessage(message)) {
				return;
			}
			onClose();
			openReportMessageModal(message);
		};
		const menuGroups: Array<MenuGroupType> = [];
		const profileItems = [
			{
				icon: <UserCircleIcon className={styles.icon} />,
				label: t`View Profile`,
				onClick: handleViewProfile,
			},
		];
		if (!isCurrentUser) {
			profileItems.push({
				icon: <ChatTeardropIcon className={styles.icon} />,
				label: isBlocked ? t`Open DM` : t`Message`,
				onClick: isBlocked ? handleOpenBlockedDm : handleMessage,
			});
			if (!isBot && relationshipType === RelationshipTypes.FRIEND) {
				profileItems.push({
					icon: <VideoCameraIcon className={styles.icon} />,
					label: t`Voice Call`,
					onClick: handleStartVoiceCall,
				});
			}
		}
		menuGroups.push({items: profileItems});
		const guildItems = [];
		if (canManageNicknames) {
			guildItems.push({
				icon: <PencilIcon className={styles.icon} />,
				label: t`Change Nickname`,
				onClick: handleChangeNickname,
			});
		}
		if (guildItems.length > 0) {
			menuGroups.push({items: guildItems});
		}
		if (!isCurrentUser && !isBot) {
			const relationshipItems = [];
			if (relationshipType === RelationshipTypes.FRIEND) {
				relationshipItems.push({
					icon: <UserMinusIcon className={styles.icon} />,
					label: t`Remove Friend`,
					onClick: handleRemoveFriend,
					danger: true,
				});
			} else if (relationshipType === RelationshipTypes.INCOMING_REQUEST) {
				relationshipItems.push({
					icon: <UserPlusIcon className={styles.icon} />,
					label: t`Accept Friend Request`,
					onClick: handleAcceptFriendRequest,
				});
			} else if (
				relationshipType !== RelationshipTypes.OUTGOING_REQUEST &&
				relationshipType !== RelationshipTypes.BLOCKED &&
				!currentUserUnclaimed &&
				Users.currentUser?.verified !== false
			) {
				relationshipItems.push({
					icon: <UserPlusIcon className={styles.icon} />,
					label: t`Send Friend Request`,
					onClick: handleSendFriendRequest,
				});
			}
			if (relationshipItems.length > 0) {
				menuGroups.push({items: relationshipItems});
			}
		}
		if (hasRoles) {
			menuGroups.push({
				items: [
					{
						icon: <UserGearIcon className={styles.icon} />,
						label: t`Manage Roles`,
						onClick: handleManageRoles,
					},
				],
			});
		}
		if (canTransfer) {
			menuGroups.push({
				items: [
					{
						icon: <CrownIcon className={styles.icon} />,
						label: t`Transfer Ownership`,
						onClick: handleTransferOwnership,
					},
				],
			});
		}
		const moderationActionKeys = resolveGuildScopedModerationActionKeys({
			hasMember: true,
			canTimeout,
			isTimedOut: memberIsTimedOut,
			canKick,
			canBan,
		});
		if (moderationActionKeys.length > 0) {
			const moderationItems = [];
			for (const actionKey of moderationActionKeys) {
				switch (actionKey) {
					case 'timeout':
						moderationItems.push({
							icon: <ClockIcon className={styles.icon} />,
							label: t`Timeout`,
							onClick: () => {
								onClose();
								ModalCommands.push(
									modal(() => (
										<TimeoutMemberSheet
											isOpen={true}
											onClose={() => ModalCommands.pop()}
											guildId={guildId}
											targetUser={user}
											targetMember={member}
										/>
									)),
								);
							},
							danger: true,
						});
						break;
					case 'remove_timeout':
						moderationItems.push({
							icon: <ClockIcon className={styles.icon} />,
							label: t`Remove Timeout`,
							onClick: () => {
								onClose();
								ModalCommands.push(modal(() => <RemoveTimeoutModal guildId={guildId} targetUser={user} />));
							},
							danger: false,
						});
						break;
					case 'kick':
						moderationItems.push({
							icon: <SignOutIcon className={styles.icon} />,
							label: t`Kick`,
							onClick: handleKickMember,
							danger: true,
						});
						break;
					case 'ban':
						moderationItems.push({
							icon: <GavelIcon className={styles.icon} />,
							label: t`Ban`,
							onClick: handleBanMember,
							danger: true,
						});
						break;
				}
			}
			menuGroups.push({items: moderationItems});
		}
		if (!isCurrentUser) {
			const reportBlockItems = [
				...(message && canReportMessage(message)
					? [
							{
								icon: <FlagIcon className={styles.icon} />,
								label: t`Report Message`,
								onClick: handleReportMessage,
								danger: true,
							},
						]
					: []),
				{
					icon: <FlagIcon className={styles.icon} />,
					label: t`Report User`,
					onClick: handleReportUser,
					danger: true,
				},
			];
			if (relationshipType === RelationshipTypes.BLOCKED) {
				reportBlockItems.push({
					icon: <ProhibitIcon className={styles.icon} />,
					label: t`Unblock`,
					onClick: handleUnblockUser,
					danger: false,
				});
			} else {
				reportBlockItems.push({
					icon: <ProhibitIcon className={styles.icon} />,
					label: t`Block`,
					onClick: handleBlockUser,
					danger: true,
				});
			}
			menuGroups.push({items: reportBlockItems});
		}
		menuGroups.push({
			items: [
				{
					icon: <IdentificationCardIcon className={styles.icon} />,
					label: t`Copy User ID`,
					onClick: handleCopyUserId,
				},
			],
		});
		const headerContent = (
			<div className={styles.header}>
				<StatusAwareAvatar user={user} size={48} guildId={guildId} />
				<div className={styles.headerInfo}>
					<span className={styles.headerName}>{NicknameUtils.getNickname(user, guildId)}</span>
					<span className={styles.headerTag}>{user.tag}</span>
				</div>
			</div>
		);
		return (
			<>
				<MenuBottomSheet isOpen={isOpen} onClose={onClose} groups={menuGroups} headerContent={headerContent} />
				<ManageRolesBottomSheet
					isOpen={manageRolesSheetOpen}
					onClose={() => setManageRolesSheetOpen(false)}
					guildId={guildId}
					userId={user.id}
				/>
			</>
		);
	},
);
